import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useVblChartOptions } from './useVblChartOptions';
import { VblAnalysisResult } from '@/types/vbl';
import { ColorByOption, MarkerByOption, SizeByOption } from '@/components/analysis/PlotCustomization';

interface VblProbabilityPlotProps {
  analysis: VblAnalysisResult;
  showConfidenceIntervals?: boolean;
  confidenceLevel?: number;
  showCensoredData?: boolean;
  censoredData?: Array<{ x: number; y: number; time: number; probability: number; caseId?: string }>;
  height?: number;
  colorBy?: ColorByOption;
  markerBy?: MarkerByOption;
  sizeBy?: SizeByOption;
  showLegend?: boolean;
}

/**
 * VBL Probability Plot - Weibull probability plot for degradation time
 * Uses enhanced chart options for consistent styling with Weibull charts
 */
export const VblProbabilityPlot: React.FC<VblProbabilityPlotProps> = ({
  analysis,
  showConfidenceIntervals = true,
  confidenceLevel = 95,
  showCensoredData = false,
  censoredData,
  height = 400,
  colorBy = 'none',
  markerBy = 'none',
  sizeBy = 'uniform',
  showLegend = false,
}) => {
  const options = useVblChartOptions({
    analysis,
    showConfidenceIntervals,
    confidenceLevel,
    showCensoredData,
    censoredData,
    chartType: 'probability',
    colorBy,
    markerBy,
    sizeBy,
    showLegend,
  });

  // Force re-render when confidence intervals change
  const chartKey = React.useMemo(
    () =>
      `vbl-probability-${showConfidenceIntervals}-${confidenceLevel}-${showCensoredData}-${Date.now()}`,
    [showConfidenceIntervals, confidenceLevel, showCensoredData]
  );

  return (
    <div style={{ height: `${height}px`, width: '100%' }}>
      <ReactECharts
        key={chartKey}
        option={options}
        style={{ height: '100%', width: '100%' }}
        theme="light"
        opts={{ renderer: 'canvas' }}
        notMerge={true}
        lazyUpdate={false}
      />
    </div>
  );
};
